<template>
  <div
    class="s-nav-tabs"
    :style="rootStyle">
    <button
      v-if="showArrows && canScrollPrev"
      aria-hidden="true"
      class="s-nav-tabs__arrow is-prev"
      tabindex="-1"
      type="button"
      @click="scrollBy(-1)">
      <sIcon
        rotate="90"
        size="10"
        type="arrow"/>
    </button>
    <div
      ref="scroller"
      class="s-nav-tabs__scroller"
      role="tablist"
      @scroll="updateArrows">
      <button
        v-for="item in items"
        :key="item.value"
        ref="tab"
        :aria-selected="item.value === value ? 'true' : 'false'"
        class="s-nav-tabs__tab h7-text-medium"
        :class="{ 'is-active': item.value === value, 'is-disabled': item.disabled }"
        :data-qa="`${dataQa}-${item.value}`"
        :disabled="item.disabled"
        role="tab"
        type="button"
        @click="select(item)">
        <!-- @slot Кастомний підпис вкладки. Параметри: item -->
        <slot
          :item="item"
          name="tab">
          {{ item.label }}
        </slot>
        <span
          class="s-nav-tabs__badge h9-text-bold"
          :data-qa="`${dataQa}-${item.value}-badge`">
          <sIcon
            v-if="item.loading"
            color="var(--secondary__green_not_active)"
            size="8"
            spin
            type="loading"/>
          <template v-else-if="item.count">{{ item.count }}</template>
        </span>
      </button>
      <span
        class="s-nav-tabs__slider"
        :style="sliderStyle"/>
    </div>
    <button
      v-if="showArrows && canScrollNext"
      aria-hidden="true"
      class="s-nav-tabs__arrow is-next"
      tabindex="-1"
      type="button"
      @click="scrollBy(1)">
      <sIcon
        rotate="270"
        size="10"
        type="arrow"/>
    </button>
    <div class="s-nav-tabs__spacer"/>
    <!-- @slot Дії праворуч від вкладок (пошук, кнопки) -->
    <slot name="actions"/>
  </div>
</template>

<script>
/**
 * Горизонтальні вкладки-навігація у стилі вкладок сторінки тендера: підпис великими літерами,
 * лічильник у верхньому індексі, анімована лінія під активною вкладкою, стрілки прокрутки,
 * слот дій праворуч. Вміст вкладок компонент не рендерить — лише перемикає value (v-model).
 * @displayName NavTabs
 */
export default {
  name: 'sNavTabs',
  data() {
    return {
      sliderLeft: 0,
      sliderWidth: 0,
      canScrollPrev: false,
      canScrollNext: false,
    }
  },
  methods: {
    select(item) {
      if (item.disabled || item.value === this.value) {
        return
      }
      /**
       * Зміна активної вкладки
       * @property {String|Number} value значення вкладки
       */
      this.$emit('input', item.value)
      this.$emit('change', item.value)
    },
    activeTabElement() {
      const index = this.items.findIndex(item => item.value === this.value)
      return index === -1 ? null : (this.$refs.tab || [])[index]
    },
    updateSlider() {
      const tab = this.activeTabElement()
      this.sliderLeft = tab ? tab.offsetLeft : 0
      this.sliderWidth = tab ? tab.offsetWidth : 0
    },
    updateArrows() {
      const scroller = this.$refs.scroller
      const tabs = this.$refs.tab || []
      if (!scroller || !tabs.length) {
        this.canScrollPrev = false
        this.canScrollNext = false
        return
      }
      // Ширину вмісту рахуємо по краю останньої вкладки, а не по scrollWidth: у scrollWidth
      // потрапляють лінія під активною вкладкою, зсунутий лічильник і субпіксельні округлення —
      // через це стрілка з'являлася, хоча всі вкладки вміщаються
      const lastTab = tabs[tabs.length - 1]
      const contentWidth = lastTab.offsetLeft + lastTab.offsetWidth
      this.canScrollPrev = scroller.scrollLeft > 1
      this.canScrollNext = scroller.scrollLeft + scroller.clientWidth < contentWidth - 1
    },
    scrollBy(direction) {
      const scroller = this.$refs.scroller
      scroller.scrollBy({ left: direction * scroller.clientWidth * 0.8, behavior: 'smooth' })
    },
    scrollActiveIntoView() {
      const tab = this.activeTabElement()
      const scroller = this.$refs.scroller
      if (!tab || !scroller) {
        return
      }
      if (tab.offsetLeft < scroller.scrollLeft) {
        scroller.scrollLeft = tab.offsetLeft
      } else if (tab.offsetLeft + tab.offsetWidth > scroller.scrollLeft + scroller.clientWidth) {
        scroller.scrollLeft = tab.offsetLeft + tab.offsetWidth - scroller.clientWidth
      }
    },
    // Перерахунок геометрії (лінія, стрілки) без прокрутки — на зміну розміру та вкладок.
    // До активної вкладки тут не прокручуємо: поява/зникнення стрілки змінює ширину області
    // і повертала б прокрутку до активної вкладки одразу після кліку по стрілці
    refresh() {
      this.$nextTick(() => {
        this.updateSlider()
        this.updateArrows()
      })
    },
    // Зміна активної вкладки (і перший рендер) — додатково прокручуємо до неї
    refreshAndReveal() {
      this.$nextTick(() => {
        this.updateSlider()
        this.scrollActiveIntoView()
        this.updateArrows()
      })
    },
  },
  computed: {
    rootStyle() {
      return { backgroundColor: this.backgroundColor, '--s-nav-tabs-color': this.color }
    },
    sliderStyle() {
      return { transform: `translateX(${this.sliderLeft}px)`, width: `${this.sliderWidth}px` }
    },
  },
  watch: {
    value: 'refreshAndReveal',
    items: {
      handler: 'refresh',
      deep: true,
    },
  },
  mounted() {
    this.refreshAndReveal()
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.refresh())
      this.resizeObserver.observe(this.$refs.scroller)
    } else {
      window.addEventListener('resize', this.refresh)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.refresh)
    }
  },
  props: {
    /**
     * Значення активної вкладки (v-model)
     */
    value: {
      type: [String, Number],
      default: null,
    },
    /**
     * Вкладки: { value, label, count?, loading?, disabled? }.
     * count — лічильник у верхньому індексі (0 і порожнє не показуються), loading — спінер замість лічильника
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Колір активної вкладки та лінії під нею
     */
    color: {
      type: String,
      default: 'var(--black-color)',
    },
    /**
     * Колір фону панелі вкладок
     */
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
    /**
     * Показувати стрілки прокрутки, коли вкладки не вміщаються
     */
    showArrows: {
      type: Boolean,
      default: true,
    },
    /**
     * Префікс data-qa: вкладка — `${dataQa}-${value}`, лічильник — `${dataQa}-${value}-badge`
     */
    dataQa: {
      type: String,
      default: 'tab',
    },
  },
}
</script>
